import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormValues, SerializeForm } from "components/ProfileForm/support";
import * as API from "lib/api/types";
import routes from "routes";
import { useAppContext, useNotification } from "utils/hooks";
import { assertPresent } from "utils/types";

export const PERMITTED_MEMBER_STATUSES: Array<API.MemberStatus> = [
  API.MemberStatus.MEMBER,
  API.MemberStatus.FORMER_MEMBER,
  API.MemberStatus.LAPSED_MEMBER,
  API.MemberStatus.MERGED,
  API.MemberStatus.DECEASED,
];

export interface ProfileUpdateState {
  fullProfileFormValues?: FormValues;
  profile?: API.UserProfile;
  profileReady: boolean;
  ready: boolean;
  handleProfileUpdate: (values: FormValues) => Promise<void>;
  resetPassword: () => Promise<void>;
  submitProfileUpdate: (payload: API.UserProfilePayload) => Promise<void>;
}

export function useProfileUpdate(): ProfileUpdateState {
  const { t } = useTranslation();
  const { api, currentUser, setCurrentUserProfile } = useAppContext();
  const { toggleNotification } = useNotification();
  const navigate = useNavigate();

  assertPresent(currentUser);
  const profile = currentUser.profile;

  const submitProfileUpdate = useCallback(
    async (payload: API.UserProfilePayload): Promise<void> => {
      if (!api) return;

      const [err, res] = await api.updateProfile(payload);

      if (err) {
        toggleNotification({
          message: t(`errors.server.${err.type}.${err.key}`),
          variant: "error",
        });
        return;
      }

      if (!res) {
        throw new Error(
          "ProfileUpdate: Profile not found for " + currentUser.profile?.email,
        );
      }

      setCurrentUserProfile(res);
      toggleNotification({
        message: t("messages.profile.success"),
        variant: "success",
      });
    },
    [api, currentUser.profile?.email, setCurrentUserProfile, t, toggleNotification],
  );

  const handleProfileUpdate = useCallback(
    async (values: FormValues): Promise<void> => {
      await submitProfileUpdate(SerializeForm.valuesToProfile(values));
    },
    [submitProfileUpdate],
  );

  const resetPassword = useCallback(async (): Promise<void> => {
    if (!api) return;

    const [err] = await api.resetPassword();

    if (err) {
      toggleNotification({
        message: t(`errors.server.${err.type}.${err.key}`),
        variant: "error",
      });
      return;
    }

    toggleNotification({
      message: t("messages.passwordReset.success", { email: profile?.email }),
      variant: "success",
    });
  }, [api, profile?.email, t, toggleNotification]);

  useEffect(() => {
    const memberStatus = profile?.memberStatus;
    if (!memberStatus) return;

    if (!PERMITTED_MEMBER_STATUSES.includes(memberStatus)) {
      navigate(`${routes.unlisted.error}?code=profileIneligible`);
    }
  }, [navigate, profile, t, toggleNotification]);

  const profileReady =
    !!profile?.memberStatus && PERMITTED_MEMBER_STATUSES.includes(profile.memberStatus);

  return {
    fullProfileFormValues: profile && SerializeForm.profileToFormValues(profile),
    profile,
    profileReady,
    ready: !!api,
    handleProfileUpdate,
    resetPassword,
    submitProfileUpdate,
  };
}
